import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { toast } from "sonner";
import {
  MapPin, Compass, ChevronRight, Loader2, Sparkles, Wallet, Clock,
  Briefcase, GraduationCap, Award, Users, Landmark, HeartPulse,
  School, Rocket, Handshake, CalendarDays, CalendarCheck, Building2, Home, Star, X,
} from "lucide-react";
import { AIThinkingState } from "@/components/AltioraComponents";
import GlobalLocationSearch from "@/components/GlobalLocationSearch";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet default markers (bundler)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const ICON_MAP = {
  briefcase: Briefcase, "graduation-cap": GraduationCap, award: Award,
  users: Users, landmark: Landmark, "heart-pulse": HeartPulse, school: School,
  rocket: Rocket, handshake: Handshake, "calendar-days": CalendarDays,
  "calendar-check": CalendarCheck, wallet: Wallet, "building-2": Building2,
  home: Home, star: Star,
};

const PRIORITY_STYLE = {
  high:   { color: "#059669", label_fr: "Priorité haute",   label_en: "High priority" },
  medium: { color: "#F2C98A", label_fr: "Priorité modérée", label_en: "Medium priority" },
  low:    { color: "#5B5B62", label_fr: "Priorité basse",   label_en: "Low priority" },
};

const RADIUS_STEPS = [1, 2, 5, 10, 25, 50];
const DEFAULT_LEVIS = { lat: 46.8033, lng: -71.1779 };

// Custom bronze pin
function makeAltioraIcon(priority = "medium") {
  const color = PRIORITY_STYLE[priority]?.color || "#0B2545";
  return L.divIcon({
    className: "altiora-marker",
    html: `<div style="
      width:28px;height:28px;border-radius:14px 14px 14px 4px;
      background:#F8F7F3;border:2px solid ${color};
      transform:rotate(-45deg);
      box-shadow:0 4px 12px rgba(0,0,0,0.5);
      display:flex;align-items:center;justify-content:center;
    "><div style="
      width:8px;height:8px;border-radius:4px;background:${color};transform:rotate(45deg);
    "></div></div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 24],
    popupAnchor: [0, -20],
  });
}

function MapAutoFly({ lat, lng, radiusKm }) {
  const map = useMap();
  useEffect(() => {
    if (!lat || !lng) return;
    map.flyTo([lat, lng], radiusKm <= 2 ? 15 : radiusKm <= 5 ? 14 : radiusKm <= 10 ? 13 : radiusKm <= 25 ? 12 : 11, { duration: 1.1 });
  }, [lat, lng, radiusKm, map]);
  return null;
}

export default function GeoGps() {
  const { lang } = useI18n();
  const [types, setTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [cost, setCost] = useState("");
  const [radius, setRadius] = useState(10);
  const [center, setCenter] = useState(null);   // {lat,lng}
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [askLocation, setAskLocation] = useState(true);
  const [currentPlaceLabel, setCurrentPlaceLabel] = useState("");
  const [enhancing, setEnhancing] = useState(false);

  const applyPlace = ({ lat, lng, label, city, country }) => {
    setCenter({ lat, lng });
    const short = city ? `${city}${country ? ", " + country : ""}` : label;
    setCurrentPlaceLabel(short || label || "");
    setAskLocation(false);
  };

  const openDetail = async (item) => {
    setSelected({ ...item, _enhancing: true });
    setEnhancing(true);
    try {
      const params = new URLSearchParams({
        lat: String(center?.lat || 0), lng: String(center?.lng || 0), enhance: "true",
      });
      const { data } = await api.get(`/geo-gps/opportunity/${item.key}?${params.toString()}`);
      setSelected(data);
    } catch {
      setSelected(item);
    } finally { setEnhancing(false); }
  };

  // Load types once
  useEffect(() => {
    (async () => {
      try {
        const t = await api.get("/geo-gps/types");
        setTypes(t.data.types || []);
        // Ensure seed exists (idempotent)
        await api.post("/geo-gps/seed").catch(() => {});
      } catch { /* silent */ }
    })();
  }, []);

  const requestLocation = () => {
    if (!navigator.geolocation) {
      setCenter(DEFAULT_LEVIS);
      setCurrentPlaceLabel("Lévis, Québec");
      setAskLocation(false);
      toast.info(lang === "fr" ? "Localisation indisponible — Lévis utilisé" : "Location unavailable — Lévis used");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setCurrentPlaceLabel(lang === "fr" ? "Ma position actuelle" : "My current location");
        setAskLocation(false);
      },
      () => {
        setCenter(DEFAULT_LEVIS);
        setCurrentPlaceLabel("Lévis, Québec");
        setAskLocation(false);
        toast.info(lang === "fr" ? "Autorisation refusée — Lévis utilisé" : "Permission denied — Lévis used");
      },
      { timeout: 8000 },
    );
  };

  // Fetch nearby when center/filters change
  useEffect(() => {
    if (!center) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.post("/geo-gps/nearby", {
          lat: center.lat, lng: center.lng, radius_km: radius,
          types: selectedTypes.length ? selectedTypes : null,
          cost: cost || null,
        });
        if (!cancelled) setItems(data.items || []);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [center, radius, selectedTypes, cost]);

  const toggleType = (k) => {
    setSelectedTypes((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);
  };

  const typeMeta = useMemo(() => {
    const m = {};
    types.forEach((t) => (m[t.key] = t));
    return m;
  }, [types]);

  return (
    <div className="min-h-full" data-testid="geo-root">
      <TopBar
        title={lang === "fr" ? "GPS socio-économique" : "Socio-economic GPS"}
        subtitle={lang === "fr" ? "Les opportunités viennent à vous — carte et pertinence expliquée." : "Opportunities come to you — map with explained relevance."}
      />

      <div className="px-6 md:px-10 py-6 md:py-8 max-w-[1800px] mx-auto">
        {/* Location prompt — initial capture (worldwide) */}
        {askLocation && (
          <div data-testid="geo-location-prompt" className="rounded-lg border border-[#0B2545]/40 bg-gradient-to-br from-white via-[#FBF6EA]/30 to-white p-6 mb-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="relative flex-shrink-0">
                <div className="absolute -inset-2 rounded-full bg-[#0B2545]/8 blur-md" />
                <Compass className="relative h-6 w-6 text-[#0B2545]" strokeWidth={1.8} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
                  {lang === "fr" ? "Où cherchons-nous ?" : "Where are we searching?"}
                </div>
                <div className="mt-0.5 font-display text-lg text-[#1F2937] tracking-tight">
                  {lang === "fr"
                    ? "Choisissez la localisation d'analyse — pour vous, ou pour un client en consultation, où qu'il soit dans le monde."
                    : "Choose the analysis location — yours, or a client's during consultation, anywhere in the world."}
                </div>
                <div className="mt-1 text-xs text-[#5B5B62]">
                  {lang === "fr"
                    ? "Aucune donnée n'est transmise à des tiers hors OpenStreetMap pour la recherche de lieu."
                    : "No data is shared with third parties beyond OpenStreetMap for place lookup."}
                </div>
              </div>
            </div>
            <GlobalLocationSearch
              lang={lang}
              autoFocus
              onSelect={applyPlace}
              className="mb-3"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <button
                data-testid="geo-enable-location"
                onClick={requestLocation}
                className="inline-flex items-center gap-1.5 border border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white text-xs font-medium px-3 py-1.5 rounded-md altiora-transition"
              >
                <MapPin className="h-3.5 w-3.5" strokeWidth={2} />
                {lang === "fr" ? "Utiliser ma position" : "Use my location"}
              </button>
              <button
                data-testid="geo-use-levis"
                onClick={() => { setCenter(DEFAULT_LEVIS); setCurrentPlaceLabel("Lévis, Québec"); setAskLocation(false); }}
                className="border border-[#E4E1D8] hover:border-[#B8B5AC] text-[#1F2937] text-xs font-medium px-3 py-1.5 rounded-md altiora-transition"
              >
                {lang === "fr" ? "Utiliser Lévis (démo)" : "Use Lévis (demo)"}
              </button>
            </div>
          </div>
        )}

        {/* Persistent location bar — change place anytime (consultation-friendly) */}
        {!askLocation && center && (
          <div data-testid="geo-location-bar" className="rounded-lg border border-[#E4E1D8] bg-white p-3 mb-6 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-3 items-center">
            <div className="flex items-center gap-2 text-xs text-[#5B5B62]">
              <MapPin className="h-3.5 w-3.5 text-[#0B2545]" />
              <span className="font-mono uppercase tracking-widest text-[10px]">{lang === "fr" ? "Analyse pour" : "Analyzing for"}</span>
              <span className="text-navy font-medium">{currentPlaceLabel || `${center.lat.toFixed(3)}, ${center.lng.toFixed(3)}`}</span>
            </div>
            <GlobalLocationSearch
              lang={lang}
              onSelect={applyPlace}
              placeholder={lang === "fr" ? "Changer — ville, adresse, pays…" : "Change — city, address, country…"}
            />
            <button
              onClick={requestLocation}
              data-testid="geo-use-my-location"
              className="inline-flex items-center gap-1.5 border border-[#E4E1D8] hover:border-navy hover:text-navy text-[#5B5B62] text-xs font-medium px-3 py-2 rounded-md transition"
            >
              <MapPin className="h-3.5 w-3.5" />
              {lang === "fr" ? "Ma position" : "My location"}
            </button>
          </div>
        )}

        <div className="grid grid-cols-12 gap-4">
          {/* ===== Filters ===== */}
          <aside className="col-span-12 lg:col-span-3 space-y-4">
            <div className="rounded-lg border border-[#E4E1D8] bg-white p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545] mb-3">
                {lang === "fr" ? "Rayon" : "Radius"}
              </div>
              <div className="flex flex-wrap gap-2">
                {RADIUS_STEPS.map((r) => (
                  <button
                    key={r}
                    data-testid={`geo-radius-${r}`}
                    onClick={() => setRadius(r)}
                    className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded border altiora-transition ${
                      radius === r
                        ? "bg-[#0B2545] border-[#0B2545] text-white"
                        : "border-[#E4E1D8] bg-[#F8F7F3] text-[#5B5B62] hover:border-[#B8B5AC]"
                    }`}
                  >
                    {r} km
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-[#E4E1D8] bg-white p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545] mb-3">
                {lang === "fr" ? "Type d'opportunité" : "Opportunity type"}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {types.map((t) => {
                  const Ico = ICON_MAP[t.icon] || Star;
                  const active = selectedTypes.includes(t.key);
                  return (
                    <button
                      key={t.key}
                      data-testid={`geo-type-${t.key}`}
                      onClick={() => toggleType(t.key)}
                      className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 text-[10px] font-mono uppercase tracking-widest altiora-transition ${
                        active
                          ? "bg-[#0B2545]/15 border-[#0B2545] text-[#0B2545]"
                          : "border-[#E4E1D8] bg-[#F8F7F3] text-[#5B5B62] hover:border-[#B8B5AC]"
                      }`}
                    >
                      <Ico className="h-3 w-3" strokeWidth={2} />
                      {t[`label_${lang}`]}
                    </button>
                  );
                })}
              </div>
              {selectedTypes.length > 0 && (
                <button
                  onClick={() => setSelectedTypes([])}
                  className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F] hover:text-[#1F2937]"
                >
                  {lang === "fr" ? "Effacer" : "Clear"}
                </button>
              )}
            </div>

            <div className="rounded-lg border border-[#E4E1D8] bg-white p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545] mb-3">
                {lang === "fr" ? "Coût" : "Cost"}
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { k: "", fr: "Tous", en: "All" },
                  { k: "free", fr: "Gratuit", en: "Free" },
                  { k: "paid", fr: "Payant", en: "Paid" },
                ].map((o) => (
                  <button
                    key={o.k || "all"}
                    data-testid={`geo-cost-${o.k || "all"}`}
                    onClick={() => setCost(o.k)}
                    className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded border altiora-transition ${
                      cost === o.k
                        ? "bg-[#0B2545] border-[#0B2545] text-white"
                        : "border-[#E4E1D8] bg-[#F8F7F3] text-[#5B5B62] hover:border-[#B8B5AC]"
                    }`}
                  >
                    {o[lang]}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-[#E4E1D8] bg-white p-5">
              <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
                  {lang === "fr" ? "Résultats" : "Results"}
                </div>
                <div className="font-mono text-[10px] text-[#5B5B62]">
                  {items.length}
                </div>
              </div>
              {loading && <AIThinkingState lang={lang} className="mt-3" />}
            </div>
          </aside>

          {/* ===== Map ===== */}
          <div className="col-span-12 lg:col-span-6">
            <div className="rounded-lg overflow-hidden border border-[#E4E1D8] bg-white h-[560px]" data-testid="geo-map-wrapper">
              {center ? (
                <MapContainer
                  center={[center.lat, center.lng]}
                  zoom={13}
                  scrollWheelZoom
                  style={{ height: "100%", width: "100%", background: "#F8F7F3" }}
                >
                  <TileLayer
                    attribution='&copy; OpenStreetMap · CartoDB'
                    url="https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png"
                  />
                  <MapAutoFly lat={center.lat} lng={center.lng} radiusKm={radius} />
                  <Circle
                    center={[center.lat, center.lng]}
                    radius={radius * 1000}
                    pathOptions={{ color: "#0B2545", weight: 1, fillColor: "#0B2545", fillOpacity: 0.05 }}
                  />
                  <Marker position={[center.lat, center.lng]} icon={L.divIcon({
                    className: "altiora-user-marker",
                    html: `<div style="width:16px;height:16px;border-radius:8px;background:#0B2545;border:3px solid #F8F7F3;box-shadow:0 0 0 4px rgba(197,168,128,0.35);"></div>`,
                    iconSize: [16, 16], iconAnchor: [8, 8],
                  })} />
                  {items.map((it) => (
                    <Marker
                      key={it.key}
                      position={[it.lat, it.lng]}
                      icon={makeAltioraIcon(it.priority)}
                      eventHandlers={{ click: () => openDetail(it) }}
                    >
                      <Popup>
                        <div className="text-xs font-body">
                          <div className="font-medium">{it.title_fr}</div>
                          <div className="text-[#8B8B8F]">{it.distance_km.toFixed(2)} km · {it.priority}</div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-xs font-mono uppercase tracking-widest text-[#8B8B8F]">
                  {lang === "fr" ? "En attente de position…" : "Waiting for position…"}
                </div>
              )}
            </div>
          </div>

          {/* ===== List ===== */}
          <div className="col-span-12 lg:col-span-3 rounded-lg border border-[#E4E1D8] bg-white max-h-[560px] overflow-y-auto">
            <div className="sticky top-0 z-10 px-4 py-3 border-b border-[#E4E1D8] bg-white font-mono text-[10px] uppercase tracking-[0.22em] text-[#5B5B62]">
              {lang === "fr" ? "Autour de vous" : "Around you"} · {items.length}
            </div>
            <div className="divide-y divide-[#E4E1D8]">
              {items.map((it) => {
                const meta = typeMeta[it.type];
                const Ico = ICON_MAP[meta?.icon] || Star;
                const p = PRIORITY_STYLE[it.priority] || PRIORITY_STYLE.medium;
                return (
                  <button
                    key={it.key}
                    onClick={() => openDetail(it)}
                    data-testid="geo-list-item"
                    className="w-full text-left px-4 py-3 hover:bg-[#EBE8DE] altiora-transition"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 h-6 w-6 rounded-md border flex items-center justify-center flex-shrink-0" style={{ borderColor: p.color, background: `${p.color}15` }}>
                        <Ico className="h-3.5 w-3.5" strokeWidth={2} style={{ color: p.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[#1F2937] leading-snug line-clamp-2">{it.title_fr}</div>
                        <div className="mt-1 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
                          <span>{it.distance_km.toFixed(1)} km</span>
                          <span>·</span>
                          <span style={{ color: p.color }}>{it.priority}</span>
                          {it.cost === "free" && <><span>·</span><span className="text-[#059669]">FREE</span></>}
                        </div>
                      </div>
                      <ChevronRight className="h-3.5 w-3.5 text-[#8B8B8F] mt-1" strokeWidth={2} />
                    </div>
                  </button>
                );
              })}
              {items.length === 0 && !loading && (
                <div className="p-6 text-xs font-mono text-[#8B8B8F]">
                  {lang === "fr" ? "Aucun résultat dans ce rayon." : "No results in this radius."}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="geo-detail-drawer">
          <div className="absolute inset-0 bg-[#1F2937]/40 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-lg h-full bg-[#F8F7F3] border-l border-[#E4E1D8] flex flex-col">
            <div className="px-5 py-4 border-b border-[#E4E1D8] flex items-start justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: PRIORITY_STYLE[selected.priority]?.color }}>
                  {typeMeta[selected.type]?.[`label_${lang}`]} · {PRIORITY_STYLE[selected.priority]?.[`label_${lang}`]}
                </div>
                <div className="mt-1 font-display text-lg text-[#1F2937] tracking-tight">{selected.title_fr}</div>
                <div className="mt-1 text-xs text-[#5B5B62]">{selected.provider}</div>
              </div>
              <button onClick={() => setSelected(null)} className="p-2 text-[#5B5B62] hover:text-[#1F2937]">
                <X className="h-4 w-4" strokeWidth={1.8} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
              {enhancing && (
                <div className="rounded-md border border-[#0B2545]/25 bg-[#0B2545]/5 p-3 flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#0B2545]">
                  <Sparkles className="h-3 w-3 animate-pulse" strokeWidth={2} />
                  {lang === "fr" ? "Orchestration IA en cours…" : "AI orchestration in progress…"}
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <MetricSm label={lang === "fr" ? "Distance" : "Distance"} value={`${selected.distance_km.toFixed(1)} km`} icon={MapPin} />
                <MetricSm label={lang === "fr" ? "Pertinence" : "Relevance"} value={`${selected.relevance_score}`} icon={Sparkles} />
                <MetricSm label={lang === "fr" ? "Confiance" : "Confidence"} value={`${Math.round((selected.explanation?.confidence || 0) * 100)}%`} icon={Compass} />
              </div>
              <Section title={lang === "fr" ? "Pourquoi cette opportunité" : "Why this opportunity"}>
                <div className="text-sm text-[#1F2937] leading-relaxed">{selected.explanation?.why}</div>
              </Section>
              <Section title={lang === "fr" ? "Impact potentiel" : "Potential impact"}>
                <div className="text-sm text-[#1F2937] leading-relaxed">{selected[`impact_${lang}`]}</div>
              </Section>
              <Section title={lang === "fr" ? "Prochaines étapes recommandées" : "Recommended next steps"}>
                <ul className="space-y-2">
                  {(selected.next_steps || []).map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#1F2937]">
                      <span className="mt-1 h-1 w-1 rounded-full bg-[#0B2545] flex-shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </Section>
              {selected.deadline && (
                <Section title={lang === "fr" ? "Date limite" : "Deadline"}>
                  <div className="inline-flex items-center gap-2 rounded-md border border-[#C5A880] bg-[#FBF6EA] text-[#7A5B14] px-3 py-1.5 font-mono text-xs">
                    <Clock className="h-3 w-3" strokeWidth={2} />
                    {new Date(selected.deadline).toLocaleDateString()}
                  </div>
                </Section>
              )}
              <Section title={lang === "fr" ? "Adresse" : "Address"}>
                <div className="text-sm text-[#1F2937]">{selected.address_fr || selected.city}</div>
                <div className="mt-1 text-[10px] font-mono text-[#8B8B8F]">
                  {selected.lat.toFixed(4)}, {selected.lng.toFixed(4)}
                </div>
              </Section>
              <Section title="Trusted Intelligence™">
                <div className="rounded-md border border-[#E4E1D8] bg-white p-4 space-y-2 text-xs">
                  <Row label={lang === "fr" ? "Données utilisées" : "Data used"} value={(selected.explanation?.data_used || []).join(", ")} />
                  <Row label={lang === "fr" ? "Hypothèses" : "Assumptions"} value={(selected.explanation?.assumptions || []).join(", ")} />
                  <Row label={lang === "fr" ? "Pour améliorer" : "To improve"} value={(selected.explanation?.improve_by || []).join(", ")} />
                  <Row label={lang === "fr" ? "Aide à la décision uniquement" : "Decision-support only"} value="Oui — jamais une garantie." accent />
                </div>
              </Section>
              {selected.url && (
                <a href={selected.url} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-2 bg-[#0B2545] hover:bg-[#123163] text-white text-sm font-medium px-5 py-2.5 rounded-md altiora-transition">
                  {lang === "fr" ? "Voir la source" : "View source"}
                  <ChevronRight className="h-4 w-4" strokeWidth={2} />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricSm({ label, value, icon: Icon }) {
  return (
    <div className="rounded-md border border-[#E4E1D8] bg-white p-3">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[9px] uppercase tracking-widest text-[#8B8B8F]">{label}</div>
        <Icon className="h-3 w-3 text-[#0B2545]" strokeWidth={2} />
      </div>
      <div className="mt-1 font-display text-lg text-[#1F2937]">{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545] mb-2">{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="col-span-1 font-mono text-[10px] uppercase tracking-widest text-[#8B8B8F]">{label}</div>
      <div className={`col-span-2 ${accent ? "text-[#0B2545]" : "text-[#1F2937]"}`}>{value || "—"}</div>
    </div>
  );
}
